import React, { useEffect, useState } from 'react';
import { History, Loader2, RefreshCw, FileDown, Mail, Search } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/common';
import { useAuth } from '../context/AuthContext';
import { useRealtime } from '../realtime/RealtimeContext';
import { useI18n } from '../i18n/I18nContext';
import { exportPDF } from '../utils/exportPDF';
import { formatDateTime } from '../utils/prefs';
import api from '../api/client';

const ACTION_META = {
  CREATE: 's-completed', UPDATE: 's-progress', DELETE: 's-stopped',
  STATUS: 's-planned', SYNC: 's-break', REORDER: 's-hold',
};
const ENTITY_META = { machine: 'var(--kmt-gold)', job: 'var(--kmt-blue)', material: 'var(--kmt-green)', schedule: 'var(--kmt-amber)' };

function fmt(ts) {
  return formatDateTime(ts);
}

export default function AuditTrail() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [entries, setEntries] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [machine, setMachine] = useState('');
  const [actor, setActor] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [emailing, setEmailing] = useState(false);

  const emailReport = async () => {
    setEmailing(true);
    try {
      const r = await api.sendAuditReport();
      toast.success(t('aud.emailed').replace('{n}', r.sent), { description: t('aud.records').replace('{n}', r.records) });
    } catch (e) {
      toast.error(t('aud.emailFailed'), { description: e?.response?.data?.detail || t('aud.sendgridHint') });
    } finally { setEmailing(false); }
  };

  const load = () => {
    setLoading(true);
    api.listAudit(filter === 'ALL' ? undefined : filter).then(setEntries).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.listMachines().then(setMachines).catch(() => {}); }, []);

  // Live sync: silently refresh the trail when anything changes
  useRealtime(() => {
    api.listAudit(filter === 'ALL' ? undefined : filter).then(setEntries).catch(() => {});
  });

  const actors = Array.from(new Set(entries.map((e) => e.actor).filter(Boolean))).sort();

  const machineMatch = (e) => {
    if (!machine) return true;
    const mObj = machines.find((m) => m.id === machine);
    const name = (mObj?.displayName || '').toLowerCase();
    // Exact ref match, or whole-word id / display-name match in the detail text (avoids false positives like M07 inside JOB-M0700).
    if (e.entity_id === machine) return true;
    const detail = (e.detail || '').toLowerCase();
    const idWord = new RegExp(`\\b${machine.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
    if (idWord.test(detail)) return true;
    return !!name && detail.includes(name);
  };
  const searchMatch = (e) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return `${e.actor || ''} ${e.action || ''} ${e.entity_type || ''} ${e.entity_id || ''} ${e.detail || ''}`.toLowerCase().includes(q);
  };
  const inRange = (e) => {
    const d = new Date(e.ts);
    if (from && d < new Date(from + 'T00:00:00')) return false;
    if (to && d > new Date(to + 'T23:59:59')) return false;
    return true;
  };
  const filtered = entries.filter((e) => inRange(e) && machineMatch(e) && (!actor || e.actor === actor) && searchMatch(e));

  const setPreset = (days) => {
    if (days === 'all') { setFrom(''); setTo(''); return; }
    const now = new Date();
    const start = new Date(now.getTime() - days * 864e5);
    const iso = (x) => x.toISOString().slice(0, 10);
    setFrom(iso(start)); setTo(iso(now));
  };

  const downloadPDF = () => {
    if (!filtered.length) { toast.error(t('aud.noRecords')); return; }
    const rangeLabel = from || to ? `${from || '…'} → ${to || '…'}` : 'All time';
    exportPDF('kmt_audit_trail.pdf', `Audit Trail Report · ${rangeLabel}`, [
      { label: 'Time', get: (r) => fmt(r.ts) },
      { label: 'Actor', get: (r) => r.actor },
      { label: 'Action', get: (r) => r.action },
      { label: 'Entity', get: (r) => r.entity_type },
      { label: 'Ref', get: (r) => r.entity_id },
      { label: 'Detail', get: (r) => r.detail },
    ], filtered);
    toast.success(t('aud.exported').replace('{n}', filtered.length));
  };

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={History} title={t('aud.title')} subtitle={`${filtered.length} ${t('aud.of')} ${entries.length} ${t('aud.changes')}`}
        right={<>
          <button className="kmt-btn-ghost flex items-center gap-2" onClick={load}><RefreshCw size={15} />{t('aud.refresh')}</button>
          {user?.role === 'ADMIN' && (
            <button className="kmt-btn-ghost flex items-center gap-2" onClick={emailReport} disabled={emailing} data-testid="audit-email-report-btn">
              {emailing ? <Loader2 className="animate-spin" size={15} /> : <Mail size={15} />}{t('aud.emailAdmins')}
            </button>
          )}
          <button className="kmt-btn-gold flex items-center gap-2" onClick={downloadPDF} data-testid="audit-download-pdf-btn"><FileDown size={16} />{t('aud.downloadPdf')}</button>
        </>} />
      <div className="p-8 space-y-5">
        <div className="flex gap-2 flex-wrap">
          {['ALL', 'machine', 'job', 'material', 'schedule'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className="px-4 py-2 rounded-lg text-sm font-medium"
              style={{ background: filter === f ? 'var(--kmt-gold)' : 'var(--kmt-panel-2)', color: filter === f ? '#17140a' : 'var(--kmt-muted)', border: '1px solid var(--kmt-border)' }}>
              {f === 'ALL' ? t('aud.all') : t(`aud.${f}`)}
            </button>
          ))}
        </div>
        {/* machine / actor / search filters */}
        <div className="kmt-panel p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="kmt-label mb-1">{t('aud.machineLabel')}</div>
            <select className="kmt-input" value={machine} onChange={(e) => setMachine(e.target.value)} data-testid="audit-machine-filter">
              <option value="">{t('aud.allMachines')}</option>
              {machines.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.displayName}</option>)}
            </select>
          </div>
          <div>
            <div className="kmt-label mb-1">{t('aud.actor')}</div>
            <select className="kmt-input" value={actor} onChange={(e) => setActor(e.target.value)} data-testid="audit-actor-filter">
              <option value="">{t('aud.allActors')}</option>
              {actors.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <div className="kmt-label mb-1">{t('aud.search')}</div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted)' }} />
              <input className="kmt-input" style={{ paddingLeft: 34 }} placeholder={t('aud.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} data-testid="audit-search-input" />
            </div>
          </div>
        </div>
        {/* date range selector */}
        <div className="kmt-panel p-4 flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div>
            <div className="kmt-label mb-1">{t('aud.from')}</div>
            <input type="date" className="kmt-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="audit-from-date" />
          </div>
          <div>
            <div className="kmt-label mb-1">{t('aud.to')}</div>
            <input type="date" className="kmt-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="audit-to-date" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {[[t('aud.today'), 0], [t('aud.d7'), 7], [t('aud.d30'), 30], [t('aud.all'), 'all']].map(([label, d]) => (
              <button key={String(d)} onClick={() => setPreset(d)} className="kmt-btn-ghost text-xs" data-testid={`audit-preset-${String(d)}`}>{label}</button>
            ))}
          </div>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : filtered.length === 0 ? (
          <div className="kmt-panel py-16 text-center" style={{ color: 'var(--kmt-muted)' }}>{t('aud.empty')}</div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                <th className="kmt-th">{t('col.time')}</th><th className="kmt-th">{t('col.actor')}</th><th className="kmt-th">{t('col.action')}</th><th className="kmt-th">{t('col.entity')}</th><th className="kmt-th">{t('col.ref')}</th><th className="kmt-th">{t('col.detail')}</th>
              </tr></thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id}>
                    <td className="kmt-td font-mono-k" data-label="Time" style={{ color: 'var(--kmt-muted)' }}>{fmt(e.ts)}</td>
                    <td className="kmt-td" data-label="Actor">{e.actor}</td>
                    <td className="kmt-td" data-label="Action"><span className={`kmt-badge ${ACTION_META[e.action] || 's-planned'}`}>{e.action}</span></td>
                    <td className="kmt-td capitalize" data-label="Entity" style={{ color: ENTITY_META[e.entity_type] || 'var(--kmt-text)' }}>{e.entity_type}</td>
                    <td className="kmt-td font-mono-k" data-label="Ref" style={{ color: 'var(--kmt-gold)' }}>{e.entity_id}</td>
                    <td className="kmt-td" data-label="Detail" style={{ color: 'var(--kmt-muted)' }}>{e.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
